import BaseClass from "../BaseClass.js";

export default class HomeCarousel extends BaseClass {
    constructor(element) {
        super(element);

        this.observer = null;
        this.visibility = 0;
        this.isIntersecting = false;

        this.currentIndex = null;
        this.isTransitioning = null;
        this.carouselContainer = null;
        this.carouselInner = null;
        this.carouselItems = null;
        this.totalSlides = null;

        this.nextBtn = null;
        this.prevBtn = null;
        this.moreBtn = null;

        this.autorunInterval = null;
        this.autorunDelay = 3000;

        this.init();
    }

    init() {
        this.createObserver();
        this.observer.observe(this.element);
    }

    action(entries) {
        entries.forEach(function (entry) {
            let jsObject = entry.target.jsObject;

            if (jsObject != undefined && jsObject.handleVisibilityChange != undefined) {
                jsObject.handleVisibilityChange(entry.intersectionRatio, entry.isIntersecting);
            }
        });
    }

    createObserver() {
        const options = {
            root: null,
            rootMargin: '0% 0% 10% 0%',
            threshold: [0.1]
        };
        this.observer = new IntersectionObserver(this.action, options);
    }

    handleVisibilityChange(visibility, isIntersecting) {
        this.visibility = visibility;
        this.isIntersecting = isIntersecting;

        if (this.visibility > 0 && this.isIntersecting && !this.carouselItems) {
            this.lazyLoadImages();
            this.setupCarousel();
            this.addEventListeners();
            this.setInitialPosition();
            this.startAutoRun();
        }
    }

    lazyLoadImages() {
        const lazyImages = this.$element.find("img[data-lazy-carousel='true']");

        lazyImages.each(function() {
            const img = this;

            if (img.dataset.src) {
                img.src = img.dataset.src;
                img.style.objectFit = 'cover';

                if ($(window).width() >= 992) {
                    img.width = '500';
                    img.height = '600';
                } else {
                    img.width = '200';
                    img.height = '200';
                }

                img.removeAttribute("data-src");
            }
        });
    }

    setupCarousel() {
        this.currentIndex = 1;
        this.isTransitioning = false;
        this.carouselContainer = this.$element.find('[data-type="carousel-container"]').get(0);
        this.carouselInner = this.$element.find('[data-type="inner-carousel"]').get(0);
        this.carouselItems = [...this.$element.find('[data-type="carousel-item"]')];
        this.appendElements();
        this.totalSlides = this.carouselItems.length;
        this.nextBtn = this.$element.find('[data-type="button-next"]');
        this.prevBtn = this.$element.find('[data-type="button-prev"]');

        const moreBtn = this.$element.find('[data-type="button-more"]');
        if (moreBtn != undefined && moreBtn.length > 0) {
            this.moreBtn = moreBtn;
        }
    }

    appendElements() {
        const firstClone = this.carouselItems[0].cloneNode(true);
        const lastClone = this.carouselItems[this.carouselItems.length - 1].cloneNode(true);

        this.carouselInner.appendChild(firstClone);
        this.carouselInner.insertBefore(lastClone, this.carouselItems[0]);

        this.carouselItems.push(firstClone);
        this.carouselItems.unshift(lastClone);
    }

    setInitialPosition() {
        this.setCarouselPosition();
        this.updateActiveClass();
    }

    addEventListeners() {
        this.nextBtn.on("click", () => {
            this.stopAutoRun();
            this.next();
        });

        this.prevBtn.on("click", () => {
            this.stopAutoRun();
            this.prev();
        });

        this.$element.on("mouseenter", () => {
            this.stopAutoRun();
        });

        this.$element.on("mouseleave", () => {
            this.startAutoRun();
        });

        this.handleResize = this.handleResize.bind(this);
        window.addEventListener("resize", () => {
            this.handleResize();
        });
    }

    handleResize() {
        this.setCarouselPosition();
        this.updateActiveClass();
    }

    setCarouselPosition() {
        const containerWidth = this.carouselContainer.offsetWidth;
        const slideWidth = this.carouselItems[0].clientWidth;
        const slideOffset = (containerWidth - slideWidth) / 2;

        this.carouselInner.style.transform = `translateX(-${
            this.currentIndex * slideWidth - slideOffset
        }px)`;
    }

    updateCarouselPosition() {
        this.carouselInner.style.transition = "all 0.5s ease-in-out";
        this.setCarouselPosition();
        this.updateActiveClass();

        if (this.moreBtn != null) {
            let carouselItem = $(this.carouselItems[this.currentIndex]);
            this.moreBtn.attr('href', carouselItem.data('url'));
        }
    }

    next() {
        if (this.isTransitioning) return;

        this.isTransitioning = true;
        this.currentIndex++;
        this.updateCarouselPosition();

        this.carouselInner.addEventListener(
            "transitionend", this.handleTransitionEnd.bind(this), { once: true }
        );
    }

    prev() {
        if (this.isTransitioning) return;

        this.isTransitioning = true;
        this.currentIndex--;
        this.updateCarouselPosition();

        this.carouselInner.addEventListener(
            "transitionend", this.handleTransitionEnd.bind(this), { once: true }
        );
    }

    startAutoRun() {
        this.autorunInterval = setInterval(() => {
            this.next();
        }, this.autorunDelay);
    }

    stopAutoRun() {
        clearInterval(this.autorunInterval);
        this.autorunInterval = null;
    }

    updateActiveClass() {
        this.carouselItems.forEach(item => {
            item.classList.remove("active");
        });

        if (this.currentIndex === 0) {
            this.carouselItems[this.totalSlides - 2].classList.add("active");
        } else if (this.currentIndex === this.totalSlides - 1) {
            this.carouselItems[1].classList.add("active");
        } else {
            this.carouselItems[this.currentIndex].classList.add("active");
        }
    }

    handleTransitionEnd() {
        if (this.currentIndex === this.totalSlides - 1) {
            this.currentIndex = 1;
            this.carouselInner.style.transition = "none";
            this.setCarouselPosition();
        }

        if (this.currentIndex === 0) {
            this.currentIndex = this.totalSlides - 2;
            this.carouselInner.style.transition = "none";
            this.setCarouselPosition();
        }

        this.isTransitioning = false;
    }

    destroyListener() {
        this.nextBtn.off("click");
        this.prevBtn.off("click");
        this.$element.off("mouseenter");
        this.$element.off("mouseleave");
        this.stopAutoRun();
        this.observer.unobserve(this.element);
        window.removeEventListener("resize", this.handleResize);
    }
}
